import { fabric } from 'fabric';
import { resizeWithSmartGuides } from '../objects/SmartGuideBehaviors';
import ShapeConnectControl from '../controls/ShapeConnectControl';
import ShapeResizeControl from '../controls/ShapeResizeControl';
import ShapeLinearResizeControl from '../controls/ShapeLinearResizeControl';
import ShapeRotateControl from '../controls/ShapeRotateControl';
import tools from '../tools';
import React from 'react';
import {
  AttachmentPoint,
  type ConnectControlWithNormalDefinition,
  type ConnectControlDefinition,
} from '../types';
import { type TextProps } from '../objects/EmbeddedTextBehaviorsV2';
import type Shape from '../objects/Shape';
import { type KeyPath } from '../collaboration/DataStoreRecordBehaviors';
import { type ToolList } from '../fabric/ObjectBehaviors';
import { DelegateFunctions, DelegateProperties } from '../utils/delegateUtils';
import {
  type RecordReferenceStruct,
  type WhiteboardRecordInfo,
  type WhiteboardRecordModels,
} from '../shared-types';
import { escapeTextContent } from '../utils/html';
import paper from 'paper';
import { getTextColorFromObject } from '../utils/getTextColor';
import { normalizeRecordType } from '../utils/record';
import { type StylusShapeOptions } from '../stylus/ShapeDrawingBrush';
import { hasRecordReference } from '../objects/guards';
import OpenAhaRecordLinkControl from '../controls/OpenAhaRecordLinkControl';
import { STROKE_TYPES } from '../constants';

const SHAPE_CONNECTOR_OFFSET = 20;
const DEFAULT_WIDTH = 100;
const DEFAULT_HEIGHT = 100;

export const DEFAULT_MARGIN = 5;

export interface ShapeControl {
  x: number;
  y: number;
  normal: { x: number; y: number };
  oppositeAttachmentPoint?: AttachmentPoint;
}

// Methods and properties delegated from ShapeObject below
interface ShapeBase
  extends Pick<
    fabric.Object,
    | 'left'
    | 'top'
    | 'width'
    | 'height'
    | 'scaleX'
    | 'scaleY'
    | 'angle'
    | 'canvas'
    | 'stroke'
    | 'fill'
    | 'z'
    | 'contentType'
    | 'attachmentId'
    | 'progress'
    | 'textLocation'
    | 'variation'
    | 'textMargin'
  > {
  set: Shape['set'];
  _renderStroke: Shape['_renderStroke'];
  _renderFill: Shape['_renderFill'];
}

// Base class for all shapes. Note that many properties and methods are delegated
// to the `fabricObject` instance. See the list of delegated properties at the
// bottom of this file. The idea is to be able to write code in ShapeBase subclasses
// as if they were actually subclasses of Shape.
class ShapeBase {
  fabricObject: Shape;

  recordInfo?: WhiteboardRecordInfo;

  booleanOperationCompatible?: boolean;

  // subclasses may override this to provide their own default properties
  static defaultProperties: Partial<Shape> = {};

  static defaultTextProps: TextProps = {
    allowedStyles: 'any',
    location: 'inside',
    textAlign: 'user',
    verticalAlign: 'user',
    fontSize: 'user',
    overflow: 'clip',
    wrapText: 'wrap',
    margins: 'scale',
  };

  textProps: TextProps = ShapeBase.defaultTextProps;

  hasExternalContent?(): boolean;
  clipExternalContent?(): boolean;
  isOnScreen?(calculate: boolean, superIsOnScreen: boolean): boolean;
  containsPoint?(pointer, lines, absolute, calculate, superContains): boolean;

  // subclasses may supply a custom tool list by implementing this
  textTools?(): ToolList;

  // allows the subclasses to specify properties for the main Shape object, which should have
  // fixed values and be non-editable
  getObjectProperties?(): Partial<Shape>;

  // subclasses may provide their own renderPath method, otherwise Shape.renderPath is used
  renderPath?(ctx: CanvasRenderingContext2D): void;

  constructor(fabricObject: Shape) {
    this.fabricObject = fabricObject;

    // Reset the shape configuration.
    this.fabricObject.uniformScaling = false;
    this.fabricObject.shadow = undefined;
    this.fabricObject.strokeWidth ||= 1;

    if (this.fabricObject.canvas) {
      this._prepareRecord();
    }
  }

  get shouldRenderText() {
    return true;
  }

  // Called if the shape was changed from another shape. Gives an opportunity
  // to override properties that other shapes might use.
  changedToShape() {
    const defaults = this.fabricObject.controller.defaultsManager.for(
      'shape',
      this.fabricObject.shapeName
    );
    if (!this.stroke && defaults.stroke) {
      this.fabricObject.set('stroke', defaults.stroke);
    }
    if (!this.fill && defaults.fill) {
      this.fabricObject.set('fill', defaults.fill);
    }
  }

  getAuthorName() {
    if (!this.fabricObject.authorId) {
      return '';
    }

    const user = this.fabricObject.controller.getResolvedUser(
      this.fabricObject.authorId
    );
    return user?.displayName || '';
  }

  /**
   * Default icon rendering
   * Renders <aha-icon> with iconClass()
   */
  static renderIcon() {
    return <aha-icon icon={this.iconClass()} />;
  }

  /**
   * Used to render the shape in the objects menu, possibly different from the tool bar.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static renderMenuIcon(options?: StylusShapeOptions) {
    return this.renderIcon();
  }

  static iconClass() {
    return 'fa-regular fa-question';
  }

  static defaultWidth() {
    return DEFAULT_WIDTH;
  }

  static defaultHeight() {
    return DEFAULT_HEIGHT;
  }

  static defaultStrokeType() {
    return STROKE_TYPES.SOLID;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static getDescription(options: StylusShapeOptions) {
    return '';
  }

  // Called when object properties change.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  wasSet(keyOrPath: KeyPath, value: unknown) {
    if (keyOrPath === 'canvas') {
      this._prepareRecord();
    }

    // Make sure card layout loads if reocrdInfo is updated
    if (keyOrPath.includes('recordType') || keyOrPath.includes('recordId')) {
      this._prepareRecord();
    }
  }

  // Object toolbar tools that should be displayed for the shape.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tools(topLevel?: boolean): ToolList {
    return [
      [tools.ToolShapeType],
      [tools.ToolOpenRecord],
      [tools.ToolBooleanShape],
      [
        tools.ToolFillColor,
        tools.ToolBorderColorWithOpacity,
        tools.ToolStrokeStyle,
      ],
    ];
  }

  get description(): string {
    return '';
  }

  allowedStyleCopyPasteFields(): string[] {
    return [
      'opacity',
      'textOpacity',
      'strokeWidth',
      'strokeType',
      'stroke',
      'fill',
    ];
  }

  // for shapes with inner borders/lines
  offset() {
    return 0;
  }

  // calculate scaled horizontal offset
  horizontalOffset() {
    const { scaleX } = this.fabricObject;
    return this.offset() / scaleX;
  }

  // calculate scaled vertical offset
  verticalOffset() {
    const { scaleY } = this.fabricObject;
    return this.offset() / scaleY;
  }

  // All shapes have embedded text by default.
  hasEmbeddedText() {
    return true;
  }

  // How to align text inside the shape if textProps.textAlign is 'shape'.
  shapeTextAlign(): 'left' | 'center' | 'right' {
    return 'center';
  }

  shapeVerticalAlign(): 'flex-start' | 'center' | 'flex-end' {
    return 'center';
  }

  // Margin to put around text. Measured in pixels - without scaling.
  calcTextMargin() {
    const margin =
      this.textMargin === undefined || this.textMargin === null
        ? DEFAULT_MARGIN
        : this.textMargin;
    return {
      top: margin,
      left: margin,
      right: margin,
      bottom: margin,
    };
  }

  // Whether the shape uses a fixed (default) size.
  hasFixedDimensions() {
    return true;
  }

  // Currently only ShapeRecord uses this for resizing horizontally
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  minDimension({ resizingWidth = true }) {
    return 1;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  maxDimension({ resizingWidth = true }) {
    return Number.MAX_SAFE_INTEGER;
  }

  // Called during any change in properties. Return changes unmodified to
  // accept changes.
  constrainChanges(changes) {
    return changes;
  }

  // Convert the polygon path to a raw paper Path but not positioned.
  paperPath(): paper.PathItem {
    console.error(
      `getPaperPath not implemented for ${this.fabricObject.shapeName}`
    );
    return new paper.Path();
  }

  // Position the path on the canvas.
  positionedPaperPath(): paper.PathItem {
    const path = this.paperPath();
    const obj = this.fabricObject;
    const width = obj.getScaledWidth() - obj.strokeWidth;
    const height = obj.getScaledHeight() - obj.strokeWidth;

    path.position = new paper.Point(
      this.left + width / 2,
      this.top + height / 2
    );
    path.scale(this.scaleX, this.scaleY);
    if (this.angle) {
      path.rotate(
        this.angle,
        new paper.Point(
          this.left - obj.strokeWidth / 2,
          this.top - obj.strokeWidth / 2
        )
      );
    }

    return path;
  }

  // Render the shape.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  render(ctx: CanvasRenderingContext2D) {
    throw new Error('Implement render() to render the shape');
  }

  // Render the author name beneath the bottom left corner of the shape boundary
  renderAuthor(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = 'gray';
    ctx.font = '14px Inter';
    ctx.fillText(this.getAuthorName(), -this.width / 2, this.height / 2 + 14);
  }

  _setBorderDash(ctx: CanvasRenderingContext2D) {
    ctx.setLineDash(this.getBorderDash());
  }

  getBorderDash() {
    switch (this.fabricObject.strokeType) {
      case STROKE_TYPES.DASHED:
        return [
          10 * Math.sqrt(this.fabricObject.strokeWidth),
          8 * Math.sqrt(this.fabricObject.strokeWidth),
        ];
      case STROKE_TYPES.DOTTED:
        return [
          this.fabricObject.strokeWidth,
          8 * Math.sqrt(this.fabricObject.strokeWidth),
        ];
      default:
        return [];
    }
  }

  renderExternalContent(): JSX.Element {
    // Override in subclass.
    return <></>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  renderClipBoundary(ctx: CanvasRenderingContext2D) {
    return false;
  }

  get recordTypename() {
    if (!this.fabricObject.recordType) return;

    return normalizeRecordType(this.fabricObject.recordType);
  }

  get recordRef(): RecordReferenceStruct | undefined {
    if (!hasRecordReference(this.fabricObject)) {
      return;
    }

    return {
      typename: this.recordTypename!,
      // Favor ref num over id, as both will work for the drawer. AI assistant
      // only works with ref nums though.
      id: this.fabricObject.referenceNum ?? this.fabricObject.recordId,
    };
  }

  async _prepareRecord() {
    // Don't refresh record cards unless user has update permissions
    if (!this.fabricObject.canvas?.allowUpdate) {
      return;
    }

    if (
      this.fabricObject.controller.allowRecordLinking() &&
      this.fabricObject.recordType &&
      this.fabricObject.recordId
    ) {
      const recordInfo = await this.fabricObject.controller.fetchRecordInfo(
        this.fabricObject.recordType,
        this.fabricObject.recordId
      );

      if (!recordInfo) {
        return;
      }

      if (['Undefined', 'NotFound'].includes(recordInfo?.typename || '')) {
        // Sending the values to the collaboration server for a
        // record the user doesn't have access to, causes all linked records
        // to be reloaded endlessly. Skip that if we got a 404 response from
        // the server.
        this.fabricObject.withoutCollaboration?.(() =>
          this.updateRecordProps(recordInfo)
        );
      } else {
        this.updateRecordProps(recordInfo);
      }

      if ('reactive' in recordInfo) {
        // We need to run on a dup'ed version of the record, since .reactive()
        // can only be called once per instance (there might be more than one shape w/ the same record)
        this.recordInfo = recordInfo.dup();
        this.makeReactive(this.recordInfo);
      }

      if (
        !this.fabricObject.persistentControls.ahaRecord &&
        !this.fabricObject.controller.canvas?.exporting &&
        !window.isPublishing
      ) {
        this.fabricObject.persistentControls.ahaRecord =
          new OpenAhaRecordLinkControl();
      }

      this.fabricObject.canvas?.requestRenderAll();
    }
  }

  updateRecordProps(record: WhiteboardRecordInfo) {
    // Avoid undo for these sets so that we don't create a separate undo group
    // which only undoes the color separately from undoing the record create.
    this.fabricObject.controller.undoManager.withoutUndo(() => {
      if (record.id) {
        if (this.fabricObject.recordId !== record.id) {
          this.fabricObject.set('recordId', record.id);
        }

        if (
          record.referenceNum &&
          this.fabricObject.referenceNum !== record.referenceNum
        ) {
          this.fabricObject.set('referenceNum', record.referenceNum);
        }

        // Always update the record name so other users can see it
        let { name } = record;
        if (record.typename === 'Page') {
          name = record.nameOrDefault;
        }

        if (name) {
          const textColor = getTextColorFromObject(this.fabricObject);
          const textContent = `<p style="color: ${textColor}">${escapeTextContent(
            name
          )}</p>`;

          if (textContent !== this.fabricObject.textContent) {
            this.fabricObject.set('textContent', textContent);
          }
        }
      }
    });
  }

  // Make a record update itself when a reactive update comes in.
  makeReactive(record: WhiteboardRecordModels) {
    record.reactive({
      reload: true,
      onUpdate: (model: WhiteboardRecordModels) => {
        this.recordInfo = model;

        this.updateRecordProps(model);

        this.canvas?.requestRenderAll();
      },
    });
  }

  static possibleAttachmentPoints: AttachmentPoint[];

  static resizeControl({ x, y }) {
    return new ShapeResizeControl({
      x,
      y,
      cursorStyleHandler: fabric.controlsUtils.scaleCursorStyleHandler,
      actionHandler: resizeWithSmartGuides(fabric.controlsUtils.scalingEqually),
    });
  }

  static rotateControl() {
    return new ShapeRotateControl({
      x: -0.5,
      y: 0.5,
      actionHandler: fabric.controlsUtils.rotationWithSnapping,
      cursorStyleHandler: fabric.controlsUtils.rotationStyleHandler,
      actionName: 'rotate',
    });
  }

  // Returns the controls to use for this class. Controls are defined at the
  // class level for efficiency - to reduce the number of Javascript objects
  // instantiated for each object on the canvas.
  // subclasses may override this, passing options to createDefaultControls to customise
  static shapeControls = this.createDefaultControls();
  static createDefaultControls(options?: {
    noRotate?: boolean;
    noLinearResize?: boolean;
  }): fabric.Controls {
    const controls: fabric.Controls = {
      tl: this.resizeControl({ x: -0.5, y: -0.5 }),
      tr: this.resizeControl({ x: 0.5, y: -0.5 }),
      bl: this.resizeControl({ x: -0.5, y: 0.5 }),
      br: this.resizeControl({ x: 0.5, y: 0.5 }),
    };

    if (!options?.noRotate) {
      controls.mtr = this.rotateControl();
    }

    if (!options?.noLinearResize) {
      Object.assign(controls, {
        mt: new ShapeLinearResizeControl({ edge: 'top' }),
        mb: new ShapeLinearResizeControl({ edge: 'bottom' }),
        ml: new ShapeLinearResizeControl({ edge: 'left' }),
        mr: new ShapeLinearResizeControl({ edge: 'right' }),
      });
    }

    // we know oppositeAttachmentPoint exists in these, but it has to be typed optional because subclasses
    // (e.g. triangles) don't have opposite attachment points
    const definitions = this.connectControlDefinition();

    this.possibleAttachmentPoints = Object.keys(
      definitions
    ) as AttachmentPoint[];
    const outboundControls = this.outboundConnectControlDefinitions();
    Object.keys(outboundControls).forEach(
      (attachmentPoint: AttachmentPoint) => {
        const controlDefinition = definitions[attachmentPoint];
        controls[attachmentPoint] = new ShapeConnectControl({
          attachmentPoint,
          x: outboundControls[attachmentPoint].x,
          y: outboundControls[attachmentPoint].y,
          offsetX: controlDefinition.normal.x * SHAPE_CONNECTOR_OFFSET,
          offsetY: controlDefinition.normal.y * SHAPE_CONNECTOR_OFFSET,
          normal: controlDefinition.normal,
          oppositeAttachmentPoint: controlDefinition.oppositeAttachmentPoint,
        });
      }
    );

    return controls;
  }

  attachmentPoints(): AttachmentPoint[] {
    // @ts-expect-error TS doesn't like us accessing the constructor this way
    return this.constructor.possibleAttachmentPoints;
  }

  // Controls shown on a selected shape to allow outbound connections.
  static outboundConnectControlDefinitions(): ConnectControlDefinition {
    return {
      [AttachmentPoint.Left]: {
        x: -0.5,
        y: 0,
      },
      [AttachmentPoint.Right]: {
        x: 0.5,
        y: 0,
      },
      [AttachmentPoint.Top]: {
        x: 0,
        y: -0.5,
      },
      [AttachmentPoint.Bottom]: {
        x: 0,
        y: 0.5,
      },
    };
  }

  // Definition of the connector controls for inbound connections.
  // Includes position & normal information so that we can steer connectors away
  // from this shape.
  //
  // For non-rectangular shapes, add or modify controls in subclasses to position
  // the connector after it is attached.
  static connectControlDefinition(): ConnectControlWithNormalDefinition {
    return {
      [AttachmentPoint.Left]: {
        x: -0.5,
        y: 0,
        normal: { x: -1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Right,
      },
      [AttachmentPoint.Right]: {
        x: 0.5,
        y: 0,
        normal: { x: 1, y: 0 },
        oppositeAttachmentPoint: AttachmentPoint.Left,
      },
      [AttachmentPoint.Top]: {
        x: 0,
        y: -0.5,
        normal: { x: 0, y: -1 },
        oppositeAttachmentPoint: AttachmentPoint.Bottom,
      },
      [AttachmentPoint.Bottom]: {
        x: 0,
        y: 0.5,
        normal: { x: 0, y: 1 },
        oppositeAttachmentPoint: AttachmentPoint.Top,
      },
      [AttachmentPoint.Center]: { x: 0, y: 0, normal: { x: 0, y: 0 } },
    };
  }
}

// Set up delegation to the fabric object for methods
DelegateProperties(ShapeBase, 'fabricObject', [
  'left',
  'top',
  'width',
  'height',
  'scaleX',
  'scaleY',
  'angle',
  'z',
  'canvas',
  'progress',
  'stroke',
  'fill',
  'textDimensions',
  'textLocation',
  'textMargin',
  'variation',
  'attachmentId',
  'contentType',
]);
DelegateFunctions(ShapeBase, 'fabricObject', [
  'set',
  '_renderStroke',
  '_renderFill',
]);

export default ShapeBase;
export { DEFAULT_WIDTH, DEFAULT_HEIGHT };
